/**
 * Government Quote Page - Deferred Carousel Loading & Navigation
 *
 * Key features:
 * - TRUE deferred DOM insertion (not show/hide)
 * - Template-based carousel content (zero init cost until cloned)
 * - IntersectionObserver triggers DOM insertion
 * - Filter changes do not force offscreen initialization
 * - Filter navigation with first-option defaulting
 */

(function () {
  'use strict';

  const CONFIG = {
    rootMargin: '200px 0px',
    threshold: 0.01,
    initAttribute: 'data-initialized'
  };

  /**
   * DeferredQuoteCarousel
   *
   * IMPORTANT: Carousel markup lives in <template> element.
   * Templates are NOT part of the DOM - no init, no measurement, no images loaded.
   * On intersection, we clone template -> append to container.
   * On filter change, we only init if the carousel is already in the viewport.
   */
  class DeferredQuoteCarousel extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.skeleton = this.querySelector('[data-carousel-skeleton]');
      this.template = this.querySelector('[data-carousel-template]');
      this.container = this.querySelector('[data-carousel-container]');
      this.observer = null;
      this.cleanups = [];
    }

    connectedCallback() {
      const inDesignMode = window.Shopify && Shopify.designMode;
      if (inDesignMode) {
        this.initializeCarousel();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.initializeCarousel();
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: CONFIG.rootMargin,
        threshold: CONFIG.threshold
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.cleanups.forEach((cleanup) => cleanup());
      this.cleanups = [];
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.initialized && !this.hidden) {
          this.initializeCarousel();
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }
      });
    }

    initializeCarousel() {
      if (this.initialized || !this.template || !this.container) return;

      this.initialized = true;
      this.setAttribute(CONFIG.initAttribute, 'true');

      const content = this.template.content.cloneNode(true);
      this.container.appendChild(content);
      this.container.hidden = false;

      if (this.skeleton) {
        this.skeleton.hidden = true;
      }

      this.initSliderProgress();
    }

    maybeInitIfInViewport() {
      if (this.initialized) return;
      const rect = this.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) {
        this.initializeCarousel();
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }
    }

    initSliderProgress() {
      const slider = this.container.querySelector('[id^="Slider-"]');
      const progressTrack = this.container.querySelector('[data-slider-progress]');
      const progressBar = this.container.querySelector('[data-slider-progress-bar]');
      if (!slider || !progressTrack || !progressBar) return;

      let isDragging = false;
      let dragPointerId = null;
      let dragStartX = 0;
      let dragStartScrollLeft = 0;

      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

      const getMetrics = () => {
        const maxScroll = slider.scrollWidth - slider.clientWidth;
        const trackWidth = progressTrack.clientWidth;
        const handleWidth = progressBar.offsetWidth || parseFloat(progressBar.style.width) || 0;
        const maxTranslateX = Math.max(trackWidth - handleWidth, 0);
        return { maxScroll, trackWidth, handleWidth, maxTranslateX };
      };

      const updateProgress = () => {
        const maxScroll = slider.scrollWidth - slider.clientWidth;
        if (maxScroll <= 0) {
          progressTrack.hidden = true;
          return;
        }

        progressTrack.hidden = false;

        const trackWidth = progressTrack.clientWidth;
        if (!trackWidth) return;

        const visibleRatio = slider.clientWidth / slider.scrollWidth;
        const minHandleWidth = 56;
        const handleWidth = Math.max(trackWidth * visibleRatio, minHandleWidth);
        const maxTranslateX = Math.max(trackWidth - handleWidth, 0);
        const progressRatio = slider.scrollLeft / maxScroll;
        const translateX = progressRatio * maxTranslateX;

        progressBar.style.width = `${handleWidth}px`;
        progressBar.style.transform = `translateX(${translateX}px)`;
      };

      const scrollToTrackPosition = (clientX) => {
        const { maxScroll, trackWidth, handleWidth } = getMetrics();
        if (maxScroll <= 0 || trackWidth <= 0) return;

        const rect = progressTrack.getBoundingClientRect();
        const maxTranslateX = Math.max(trackWidth - handleWidth, 0);
        const rawTranslateX = clientX - rect.left - handleWidth / 2;
        const translateX = clamp(rawTranslateX, 0, maxTranslateX);
        const ratio = maxTranslateX > 0 ? translateX / maxTranslateX : 0;
        slider.scrollLeft = ratio * maxScroll;
      };

      const onTrackPointerDown = (event) => {
        // Ignore direct bar drags here; handled by bar pointer handlers.
        if (event.target === progressBar) return;
        event.preventDefault();
        scrollToTrackPosition(event.clientX);
      };

      const onBarPointerDown = (event) => {
        event.preventDefault();
        isDragging = true;
        dragPointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScrollLeft = slider.scrollLeft;
        progressBar.setPointerCapture(event.pointerId);
      };

      const onBarPointerMove = (event) => {
        if (!isDragging || event.pointerId !== dragPointerId) return;
        const { maxScroll, maxTranslateX } = getMetrics();
        if (maxScroll <= 0 || maxTranslateX <= 0) return;

        const deltaX = event.clientX - dragStartX;
        const deltaRatio = deltaX / maxTranslateX;
        slider.scrollLeft = dragStartScrollLeft + deltaRatio * maxScroll;
      };

      const onBarPointerEnd = (event) => {
        if (!isDragging || event.pointerId !== dragPointerId) return;
        isDragging = false;
        dragPointerId = null;
        if (progressBar.hasPointerCapture(event.pointerId)) {
          progressBar.releasePointerCapture(event.pointerId);
        }
      };

      const onScroll = () => {
        window.requestAnimationFrame(updateProgress);
      };

      slider.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', updateProgress);
      progressTrack.addEventListener('pointerdown', onTrackPointerDown);
      progressBar.addEventListener('pointerdown', onBarPointerDown);
      progressBar.addEventListener('pointermove', onBarPointerMove);
      progressBar.addEventListener('pointerup', onBarPointerEnd);
      progressBar.addEventListener('pointercancel', onBarPointerEnd);
      this.cleanups.push(() => slider.removeEventListener('scroll', onScroll));
      this.cleanups.push(() => window.removeEventListener('resize', updateProgress));
      this.cleanups.push(() => progressTrack.removeEventListener('pointerdown', onTrackPointerDown));
      this.cleanups.push(() => progressBar.removeEventListener('pointerdown', onBarPointerDown));
      this.cleanups.push(() => progressBar.removeEventListener('pointermove', onBarPointerMove));
      this.cleanups.push(() => progressBar.removeEventListener('pointerup', onBarPointerEnd));
      this.cleanups.push(() => progressBar.removeEventListener('pointercancel', onBarPointerEnd));

      updateProgress();
      window.requestAnimationFrame(updateProgress);
    }

  }

  if (!customElements.get('deferred-quote-carousel')) {
    customElements.define('deferred-quote-carousel', DeferredQuoteCarousel);
  }

  /**
   * GovtQuoteNavigation
   * Manages filter state and carousel visibility
   */
  class GovtQuoteNavigation {
    constructor() {
      this.nav = document.getElementById('GovtQuoteNav');
      if (!this.nav) return;

      this.exploreSelect = this.nav.querySelector('[data-quote-explore]');
      this.filters = this.nav.querySelector('[data-quote-filters]');
      this.filterGroups = this.nav.querySelectorAll('.govt-quote-nav__filter-group');
      this.filterButtons = this.nav.querySelectorAll('.govt-quote-nav__filter-btn');
      this.carousels = document.querySelectorAll('deferred-quote-carousel');
      this.navProgress = this.nav.querySelector('[data-nav-progress]');
      this.navProgressBar = this.nav.querySelector('[data-nav-progress-bar]');
      this.navProgressRaf = null;
      this.navProgressDragging = false;
      this.navProgressDragPointerId = null;
      this.navProgressDragStartX = 0;
      this.navProgressDragStartScrollLeft = 0;

      this.allowedCategories = new Set(['layer', 'environment', 'level']);

      this.state = {
        category: 'layer',
        filter: 'all'
      };

      this.init();
    }

    init() {
      this.state.category = this.getInitialCategory();
      this.normalizeState();
      this.detectDefaults();
      this.ensureValidFilterForCategory();
      this.bindEvents();
      this.bindNavProgress();
      this.applyState();
    }

    getInitialCategory() {
      const firstOptionValue = this.exploreSelect?.options?.[0]?.value;
      if (firstOptionValue && this.allowedCategories.has(firstOptionValue)) {
        return firstOptionValue;
      }
      return 'layer';
    }

    normalizeState() {
      if (!this.allowedCategories.has(this.state.category)) {
        this.state.category = 'layer';
      }
      if (!this.state.filter) {
        this.state.filter = 'all';
      }
    }

    detectDefaults() {
      const defaultBtn = this.nav.querySelector(
        `.govt-quote-nav__filter-btn[data-filter-category="${this.state.category}"][data-is-default="true"]`
      );
      if (defaultBtn) {
        this.state.filter = defaultBtn.dataset.filterValue;
      }
    }

    bindEvents() {
      if (this.exploreSelect) {
        this.exploreSelect.addEventListener('change', (event) => {
          this.state.category = event.target.value;
          this.ensureValidFilterForCategory();
          this.applyState();
        });
      }

      this.filterButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.dataset.filterCategory !== this.state.category) {
            this.state.category = btn.dataset.filterCategory;
          }
          this.state.filter = btn.dataset.filterValue;
          this.ensureValidFilterForCategory();
          this.applyState();
          this.scrollToActiveCarousel();
        });
      });
    }

    bindNavProgress() {
      if (!this.filters || !this.navProgress || !this.navProgressBar) return;

      this.filters.addEventListener('scroll', () => {
        this.scheduleNavProgressUpdate();
      }, { passive: true });

      window.addEventListener('resize', () => {
        this.scheduleNavProgressUpdate();
      });

      this.navProgress.addEventListener('pointerdown', (event) => {
        if (event.target === this.navProgressBar) return;
        event.preventDefault();
        this.scrollNavByTrackPosition(event.clientX);
      });

      this.navProgressBar.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.navProgressDragging = true;
        this.navProgressDragPointerId = event.pointerId;
        this.navProgressDragStartX = event.clientX;
        this.navProgressDragStartScrollLeft = this.filters.scrollLeft;
        this.navProgressBar.setPointerCapture(event.pointerId);
      });

      this.navProgressBar.addEventListener('pointermove', (event) => {
        if (!this.navProgressDragging || event.pointerId !== this.navProgressDragPointerId) return;
        const metrics = this.getNavProgressMetrics();
        if (metrics.maxScroll <= 0 || metrics.maxTranslateX <= 0) return;

        const deltaX = event.clientX - this.navProgressDragStartX;
        const deltaRatio = deltaX / metrics.maxTranslateX;
        this.filters.scrollLeft = this.navProgressDragStartScrollLeft + deltaRatio * metrics.maxScroll;
      });

      const endDrag = (event) => {
        if (!this.navProgressDragging || event.pointerId !== this.navProgressDragPointerId) return;
        this.navProgressDragging = false;
        this.navProgressDragPointerId = null;
        if (this.navProgressBar.hasPointerCapture(event.pointerId)) {
          this.navProgressBar.releasePointerCapture(event.pointerId);
        }
      };

      this.navProgressBar.addEventListener('pointerup', endDrag);
      this.navProgressBar.addEventListener('pointercancel', endDrag);
    }

    getNavProgressMetrics() {
      if (!this.filters || !this.navProgress || !this.navProgressBar) {
        return { maxScroll: 0, trackWidth: 0, handleWidth: 0, maxTranslateX: 0 };
      }

      const maxScroll = this.filters.scrollWidth - this.filters.clientWidth;
      const trackWidth = this.navProgress.clientWidth;
      const handleWidth = this.navProgressBar.offsetWidth || parseFloat(this.navProgressBar.style.width) || 0;
      const maxTranslateX = Math.max(trackWidth - handleWidth, 0);
      return { maxScroll, trackWidth, handleWidth, maxTranslateX };
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    scrollNavByTrackPosition(clientX) {
      const metrics = this.getNavProgressMetrics();
      if (metrics.maxScroll <= 0 || metrics.trackWidth <= 0) return;

      const rect = this.navProgress.getBoundingClientRect();
      const maxTranslateX = Math.max(metrics.trackWidth - metrics.handleWidth, 0);
      const rawTranslateX = clientX - rect.left - metrics.handleWidth / 2;
      const translateX = this.clamp(rawTranslateX, 0, maxTranslateX);
      const ratio = maxTranslateX > 0 ? translateX / maxTranslateX : 0;
      this.filters.scrollLeft = ratio * metrics.maxScroll;
    }

    scheduleNavProgressUpdate() {
      if (this.navProgressRaf) cancelAnimationFrame(this.navProgressRaf);
      this.navProgressRaf = requestAnimationFrame(() => {
        this.updateNavProgress();
      });
    }

    updateNavProgress() {
      if (!this.filters || !this.navProgress || !this.navProgressBar) return;

      const maxScroll = this.filters.scrollWidth - this.filters.clientWidth;
      if (maxScroll <= 0) {
        this.navProgress.hidden = true;
        return;
      }

      this.navProgress.hidden = false;

      const trackWidth = this.navProgress.clientWidth;
      if (!trackWidth) return;

      const visibleRatio = this.filters.clientWidth / this.filters.scrollWidth;
      const minHandleWidth = 56;
      const handleWidth = Math.max(trackWidth * visibleRatio, minHandleWidth);
      const maxTranslateX = Math.max(trackWidth - handleWidth, 0);
      const progressRatio = this.filters.scrollLeft / maxScroll;
      const translateX = progressRatio * maxTranslateX;

      this.navProgressBar.style.width = `${handleWidth}px`;
      this.navProgressBar.style.transform = `translateX(${translateX}px)`;
    }

    ensureValidFilterForCategory() {
      const categoryButtons = Array.from(this.filterButtons).filter(
        (btn) => btn.dataset.filterCategory === this.state.category
      );

      if (!categoryButtons.length) {
        this.state.filter = 'all';
        return;
      }

      const currentIsValid = categoryButtons.some(
        (btn) => btn.dataset.filterValue === this.state.filter
      );
      if (currentIsValid) return;

      const defaultBtn = categoryButtons.find((btn) => btn.dataset.isDefault === 'true');
      if (defaultBtn) {
        this.state.filter = defaultBtn.dataset.filterValue;
        return;
      }

      const allBtn = categoryButtons.find((btn) => btn.dataset.filterValue === 'all');
      if (allBtn) {
        this.state.filter = allBtn.dataset.filterValue;
        return;
      }

      this.state.filter = categoryButtons[0].dataset.filterValue;
    }

    getVisibleCategoryCarousels() {
      return Array.from(this.carousels).filter(
        (carousel) => carousel.dataset.category === this.state.category
      );
    }

    getActiveTargetCarousel() {
      const categoryCarousels = this.getVisibleCategoryCarousels();
      if (!categoryCarousels.length) return null;

      if (this.state.filter && this.state.filter !== 'all') {
        const exactMatch = categoryCarousels.find(
          (carousel) => carousel.dataset.filter === this.state.filter
        );
        if (exactMatch) return exactMatch;
      }

      return categoryCarousels[0];
    }

    scrollToActiveCarousel() {
      const target = this.getActiveTargetCarousel();
      if (!target) return;

      let offset = 24;
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) {
        offset += stickyHeader.getBoundingClientRect().height;
      }

      const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth'
      });
    }

    applyState() {
      if (this.exploreSelect) {
        this.exploreSelect.value = this.state.category;
      }

      this.filterGroups.forEach((group) => {
        group.hidden = group.dataset.filterGroup !== this.state.category;
      });

      this.filterButtons.forEach((btn) => {
        const isActive =
          btn.dataset.filterCategory === this.state.category &&
          btn.dataset.filterValue === this.state.filter;
        btn.classList.toggle('govt-quote-nav__filter-btn--active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.carousels.forEach((carousel) => {
        const matches = carousel.dataset.category === this.state.category;
        carousel.hidden = !matches;

        if (matches) {
          carousel.maybeInitIfInViewport();
        }
      });

      this.scheduleNavProgressUpdate();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new GovtQuoteNavigation());
  } else {
    new GovtQuoteNavigation();
  }
})();
